import { useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Upload control used on the Profile page. Clicking the avatar (or the small
 * camera badge) opens the file picker; on selection we POST to
 * `/auth/me/avatar` which crops + resizes server-side, and the parent's
 * `setUser(updatedUser)` keeps every other "current user" reference in sync.
 */
export default function AvatarUploader({ user, setUser, size = 80 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const px = `${size}px`;
  const initial = (user?.name || "·")[0].toUpperCase();
  const path = user?.avatar_path;

  const pick = () => {
    if (busy) return;
    inputRef.current?.click();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image (PNG, JPG, WebP, GIF).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (max 8MB).");
      return;
    }
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/auth/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(data);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    if (!window.confirm("Remove your photo and go back to your initial?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/me/avatar");
      setUser(data);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block group" data-testid="avatar-uploader">
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        style={{ width: px, height: px }}
        className="block rounded-full overflow-hidden bg-burgundy text-cream font-serif flex items-center justify-center ring-2 ring-cream shadow hover:opacity-90 transition disabled:opacity-60"
        aria-label="Change your photo"
        data-testid="avatar-uploader-trigger"
      >
        {path ? (
          <img src={fileUrl(path)} alt={user?.name || ""} className="w-full h-full object-cover" />
        ) : (
          <span className="text-3xl">{initial}</span>
        )}
      </button>

      <button
        type="button"
        onClick={pick}
        className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-gold text-jjblue flex items-center justify-center shadow border-2 border-cream cursor-pointer group-hover:scale-105 transition"
        title="Change photo"
        aria-label="Change photo"
        data-testid="avatar-uploader-camera"
      >
        <Camera className="h-3.5 w-3.5" strokeWidth={1.8} />
      </button>

      {path && !busy && (
        <button
          type="button"
          onClick={remove}
          className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-cream text-burgundy border border-jj shadow md:opacity-0 md:group-hover:opacity-100 transition flex items-center justify-center"
          title="Remove photo"
          aria-label="Remove photo"
          data-testid="avatar-uploader-remove"
        >
          <Trash2 className="h-3 w-3" strokeWidth={1.8} />
        </button>
      )}

      {busy && (
        <span className="absolute inset-0 rounded-full bg-jjblue/40 flex items-center justify-center text-cream text-[10px] font-medium" data-testid="avatar-uploader-busy">
          Uploading…
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={upload}
        data-testid="avatar-uploader-input"
      />
    </div>
  );
}
